import debouncer from "../../Utilities/debouncer";
import { createSpinner, createTdWithText, createImg, createTdWithElement, createTableBodyLoadingIndicator } from "../../DomUtilities/ElementsCreator";
import { insertBody } from "../../DomUtilities/ElementsUtil";
import ITableSort from "../../Sorting/ITableSort";
import ITablePaginator from "../../Pagination/ITablePaginator";
import TablePaginator from "../../Pagination/TablePaginator";
import TableSort from "../../Sorting/TableSort";
import openErrorModal from "../../Error/ErrorModal";
import { fetchOrdersList } from "../../HTTPRequests/OrderRequests";
import PageModel from "../../Models/ViewModels/PageModel";
import OrdersScanHandler from "./OrdersScanHandler";
import Mediator from "../../Utilities/Mediator";
import OrderOverviewModal from "./OrderOverviewModal";
import { getStoresNames } from "../../HTTPRequests/StoreRequests";
import { getUsersNames } from "../../HTTPRequests/UserRequests";
import OrderUpdateModal from "./Update/OrderUpdateModal";
import OrderViewModel from "../../Models/ViewModels/OrderViewModel";

export default class OrdersOverview implements ITableSort, ITablePaginator {
	table: HTMLElement;
	searchBar: HTMLInputElement;
	searchStoreCheckBox: HTMLInputElement;
	createBtn: HTMLButtonElement;
	searchValue: string;
	status: number;
	filterOn: boolean;
	paginator: TablePaginator;
	sortHandler: TableSort;
	userNamesTds: HTMLTableCellElement[];
	userIds: Set<string>;
	private readonly FINISHED_FILTER: number = 2;

	constructor() {
		this.table = document.getElementById('orders');
		this.searchBar = document.querySelector('input.search-bar');
		this.searchStoreCheckBox = document.querySelector('#search-store');
		this.createBtn = document.querySelector('.create-btn');
		this.searchValue = this.SearchValue;
		this.paginator = new TablePaginator(this);
		this.sortHandler = new TableSort(this);
		this.status = null;

		this.init();
	}

	private init(): void {
		Mediator.on("orderOverviewOperationFinished", () => this.fetchList());
		this.fetchList();
		this.addSearchInputListener();
		this.sortHandler.addSortingIconsClickListeners();
		this.paginator.addPaginationEventsListeners();
		this.addSatusFilterClickListeners();
		this.addSearchStoreCheckBoxChangeListener();
		new OrdersScanHandler();
	}

	private addSearchStoreCheckBoxChangeListener() {
		if (this.searchStoreCheckBox) {
			this.searchStoreCheckBox.onchange = () => {
				if (this.searchValue) {
					this.fetchList();
				}
			}
		}
	}

	private addSatusFilterClickListeners() {
		const filters: NodeListOf<HTMLDivElement> = document.querySelectorAll(".status-filter div");

		filters.forEach((filter, index) => {
			filter.onclick = () => {
				filters.forEach(d => d.classList.remove("--clicked"));
				if (this.status === index && this.filterOn) {
					this.status = null;
					this.filterOn = false;
				} else {
					filter.classList.add("--clicked");
					this.status = index;
					this.filterOn = true;
				}
				this.replaceCoulmnNameAndValue();
				this.fetchList();
			}
		});
	}

	private replaceCoulmnNameAndValue() {
		const dateHeaderColumn: HTMLTableHeaderCellElement = this.table.querySelector("th > span[data-created]");
		const sortImage: HTMLImageElement = this.table.querySelector("th > img[data-created]");
		if (this.status === this.FINISHED_FILTER && this.filterOn) {
			dateHeaderColumn.innerText = dateHeaderColumn.dataset.finished;
			sortImage.dataset.value = sortImage.dataset.finished;
		} else {
			dateHeaderColumn.innerText = dateHeaderColumn.dataset.created;
			sortImage.dataset.value = sortImage.dataset.created;
		}
	}

	private get isSearchByStoreEnabled(): boolean {
		return !!this.searchStoreCheckBox && this.searchStoreCheckBox.checked;
	}

	public fetchList() {
		this.showLoadingIndicator();
		fetchOrdersList(encodeURIComponent(this.SearchValue), this.sortHandler.sortValue, this.sortHandler.sortAscending, this.paginator.currentPage, this.paginator.pageSize, this.status, this.isSearchByStoreEnabled)
			.then((response: PageModel<OrderViewModel>) => {
				this.updateTable(response);
			}, () => { //backend error
				openErrorModal();
			}).catch(() => { //frontend error
				openErrorModal();
			});
	}

	private showLoadingIndicator() {
		insertBody(this.table, createTableBodyLoadingIndicator());
	}

	private updateTable(data: PageModel<OrderViewModel>) {
		this.paginator.currentPage = data.pageNumber;
		this.paginator.total = data.total;
		this.paginator.pageSize = data.pageSize;
		this.renderTable(data.list);
	}

	private renderTable(list: Array<OrderViewModel>) {
		insertBody(this.table,
			this.renderBody(list)
		);
		this.paginator.renderFoot();
	}

	private renderBody(ordersList: Array<OrderViewModel>) {
		const tbody = document.createElement('tbody');
		const headers = this.table.querySelectorAll('th');

		if (ordersList.length) {
			this.userNamesTds = new Array<HTMLTableCellElement>();
			this.userIds = new Set<string>();
			for (const order of ordersList) {
				const { row, editIcon } = this.createOrderTableRow(order, headers.length === 7);

				tbody.append(row);
				new OrderOverviewModal().init(row);
				new OrderUpdateModal().init(editIcon);
			}
			this.fetchUsersList();
		} else {
			const emptyText = this.table.dataset.empty;
			const tr = document.createElement('tr');
			const td = createTdWithText(emptyText);
			td.colSpan = this.table.querySelectorAll('th').length;
			td.style.textAlign = 'center';
			tr.append(td);
			tbody.append(tr);
		}

		return tbody;
	}

	private createOrderTableRow(order: OrderViewModel, isDomainAdmin: boolean): { row: HTMLTableRowElement, editIcon: HTMLImageElement } {
		const row = document.createElement('tr');
		const img = createImg('/assets/Info.svg', 'info icon');
		const editIcon = createImg('/assets/edit.svg', 'edit icon');
		row.classList.add('table__row');
		row.id = order.id;
		editIcon.dataset.id = order.id;

		const tdCusName = createTdWithText(order.customerName);
		row.append(tdCusName);

		const tdStatus = createTdWithText(order.status);
		row.append(tdStatus);

		const tdNo = createTdWithText(order.id);
		row.append(tdNo);

		const tdDate = createTdWithText(order.date);
		row.append(tdDate);

		const tdUser = document.createElement('td');
		if (order.userId) {
			tdUser.append(createSpinner());
			tdUser.dataset.userId = order.userId;
			this.userIds.add(order.userId);
			this.userNamesTds.push(tdUser);
		} else {
			tdUser.innerText = order.customerName;
		}
		row.append(tdUser);

		if (isDomainAdmin) {
			const tdStore = document.createElement('td');
			order.storeName != null ? tdStore.innerText = order.storeName : tdStore.innerHTML = this.table.dataset.domain;
			row.append(tdStore);
		}

		const lastTd = createTdWithElement(editIcon, img)
		row.append(lastTd);

		return { row, editIcon };
	}

	private fetchUsersList() {
		getUsersNames([...this.userIds])
			.then((userNames) => {
				this.userNamesTds.forEach(td => {
					td.innerHTML = "";
					td.innerText = userNames.get(td.dataset.userId) || "";
				});

			}, () => {
				this.userNamesTds.forEach(td => td.innerHTML = "");
			}).catch(() => {
				this.userNamesTds.forEach(td => td.innerHTML = "");
			});
	}

	private get SearchValue(): string {
		return this.searchBar.value.trim();
	}

	private addSearchInputListener() {
		const debounce = debouncer(this.searchList.bind(this), 400, false);

		this.searchBar.oninput = (event) => {
			if ((event.target["value"]).trim() != this.searchValue) {
				debounce(event);
			}
		}

		this.searchBar.onkeydown = (event) => {
			event.stopPropagation();
		}
	}

	private searchList() {
		this.searchValue = this.SearchValue;
		this.fetchList();
	}
}
